import React, { useCallback, useMemo } from 'react';
import { CompositeDataSetDagsterSourceRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { z } from 'zod';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeCompleteIndicator } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeCompleteIndicator';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { OrganizationEntitledDagsterTransforms } from 'libs/end-to-end/src/external-dagster-transforms';
import { DagsterTransformSelector } from 'apps/sigma2-ui/src/data-wizard/components/DagsterTransformSelector';

const DagsterSourceConfigStep = z.enum(['config', 'complete']);

type DagsterSourceConfigStep = z.infer<typeof DagsterSourceConfigStep>;

export const DataSetCompositeRecipeDagsterSourceConfigComponent = ({
  organizationId,
  config,
  setConfig,
}: {
  organizationId: string;
  config: CompositeDataSetDagsterSourceRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
}) => {
  const getEntitledDagsterTransformsUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/dagster-transforms`;
  }, [organizationId]);

  const entitledDagsterTransformsPattern = useSimpleReadPattern({
    getUrlFn: getEntitledDagsterTransformsUrlFn,
    canRead: true,
    responseSchema: OrganizationEntitledDagsterTransforms,
  });

  const entitledDagsterTransforms = useMemo(() => {
    return entitledDagsterTransformsPattern.status === 'done' &&
      entitledDagsterTransformsPattern.entity
      ? entitledDagsterTransformsPattern.entity
      : [];
  }, [entitledDagsterTransformsPattern]);

  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<DagsterSourceConfigStep>[] =
    useMemo(() => {
      return [
        { value: 'config', display: 'Select Dagster Transform' },
        { value: 'complete', display: 'Complete' },
      ];
    }, []);

  const dataWizardPayload =
    useDataSetCompositeRecipeDataWizardTracker<DagsterSourceConfigStep>({
      items: stepItems,
      onNext: () => true,
    });

  const currentUiToShow = useMemo(() => {
    if (dataWizardPayload.selectedItem === null) {
      return null;
    }

    switch (dataWizardPayload.selectedItem) {
      case 'config':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <DagsterTransformSelector
              entitledTransforms={entitledDagsterTransforms}
              selectedTransformId={config.dagsterTransformId || null}
              setSelectedTransformId={(id: string | null) => {
                setConfig({
                  ...config,
                  dagsterTransformId: id ?? '',
                });
              }}
              isDisabled={false}
            />
          </Spacing>
        );
      case 'complete':
        return <DataSetCompositeCompleteIndicator />;
    }
  }, [
    config,
    dataWizardPayload.selectedItem,
    entitledDagsterTransforms,
    setConfig,
  ]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <DataSetCompositeRecipeDataWizardTracker payload={dataWizardPayload} />
      {currentUiToShow}
      {dataWizardPayload.paginationButtons}
    </Spacing>
  );
};
